import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  cpGetTenantSettings,
  cpUpdateTenantSettings,
  type TenantSettingsUpdate,
} from '@/lib/api/controlPlaneClient'
import { useAuthStore } from '@/lib/store/authStore'

export const TENANT_SETTINGS_QUERY_KEY = ['storefront-tenant-settings'] as const

export function useStorefrontTenantSettings() {
  const queryClient = useQueryClient()
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  const storefrontKeyParam = activeStorefrontKey ?? undefined

  const fullQueryKey = [...TENANT_SETTINGS_QUERY_KEY, activeStorefrontKey ?? '__primary__'] as const

  const settingsQuery = useQuery({
    queryKey: fullQueryKey,
    queryFn: () => cpGetTenantSettings(storefrontKeyParam),
  })

  const updateMutation = useMutation({
    mutationFn: (data: TenantSettingsUpdate) => cpUpdateTenantSettings(data, storefrontKeyParam),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_SETTINGS_QUERY_KEY })
    },
  })

  return {
    settings: settingsQuery.data ?? null,
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    queryKey: fullQueryKey,

    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error?.message ?? null,
  }
}
